import { useLocation, useNavigate } from 'react-router-dom';

import { DEFAULT_CENTER, DEFAULT_ZOOM, MAP_STYLE } from 'components/Map/constants';

import useApplication from 'hooks/useApplication';
import useBoundary from 'hooks/useBoundary';
import useField from 'hooks/useField';
import { useFloatingResourceAddContext } from 'hooks/useFloatingResourceAddContext';
import { useNavigationCollapse } from 'hooks/useNavigationCollapse';
import usePageRoutes from 'hooks/usePageRoutes';

import { EMPTY_PARTS } from './constants';
import type {
    CreateBoundaryLocationState,
    CreateBoundaryRequest,
    Geometry,
    MapInteractionOptions,
    MapOptions,
    UseCreateBoundaryData
} from './types';

const useData = (): UseCreateBoundaryData => {
    const { state } = useLocation();
    const navigate = useNavigate();
    const { boundariesPage, boundaryDetailsPage } = usePageRoutes();
    const { createBoundary } = useBoundary();
    const { assignFieldToBoundary } = useField();
    const { assignBoundaryToApplication } = useApplication();

    const { applicationId, name, siteId } = (state ?? {}) as CreateBoundaryLocationState;

    useFloatingResourceAddContext(false);
    useNavigationCollapse(true);

    const geometry: Geometry = {
        isEditable: true,
        parts: EMPTY_PARTS
    };

    const mapOptions: MapOptions = {
        accessToken: import.meta.env.VITE_MAPBOX_ACCESS_TOKEN as string,
        center: DEFAULT_CENTER,
        mapStyle: MAP_STYLE,
        zoom: DEFAULT_ZOOM
    };

    const mapInteractionOptions: MapInteractionOptions = {
        hasAddressSearch: true,
        hasDrawingControls: true,
        hasExitConfirmation: true,
        hasMapControls: true
    };

    const handleClose = () => {
        navigate(boundariesPage);
    };

    const handleMapComplete = async (result: CreateBoundaryRequest) => {
        if (!name) {
            navigate(boundariesPage);

            return;
        }

        const boundary = await createBoundary({
            boundary: { boundaryParts: result.boundaryParts, name }
        });

        if (!boundary) {
            return;
        }

        const assignments: Promise<unknown>[] = [];

        if (applicationId) {
            assignments.push(
                assignBoundaryToApplication({ applicationId, boundaryId: boundary.id })
            );
        }

        if (siteId) {
            assignments.push(assignFieldToBoundary({ boundaryId: boundary.id, siteId }));
        }

        await Promise.all(assignments);

        navigate(boundaryDetailsPage(boundary.id));
    };

    return {
        geometry,
        handleClose,
        handleEditMapClose: handleClose,
        handleMapComplete,
        mapInteractionOptions,
        mapOptions
    };
};

export default useData;
